/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  onDisconnect,
  withOnUpdate,
  withReset,
} from '@reatom/framework'

import { Array, Set } from '@blade/std-lib'
import {
  startNewWalletFlowEvent,
  startWalletDepositFlowEvent,
  startWalletTransferFlowEvent,
  loadWalletsAction,
  walletsAtom,
  type WalletId,
  type WalletModel,
} from '@root/entities/wallet'
import { WalletManagerRoute } from '@root/features/routing'
import { walletDeletedEvent } from '@root/features/wallet'
import { routerActions } from '@root/shared/lib/react-router'

export type WalletDetailsScreen =
  | 'transactions'
  | 'edit'
  | 'export'
  | 'deposit'
  | 'transfer'
  | 'swap'
  | 'new-wallet'
  | 'import-wallet'
  | 'transactions'

const reatomWalletsManager = () => {
  const selectedWalletIdsAtom = atom(Set.create<WalletId>([])).pipe(withReset())
  const walletDetailsScreenAtom = atom<WalletDetailsScreen>(
    'transactions',
    'walletDetailsScreenAtom',
  ).pipe(withReset())

  const selectedWalletsAtom = atom((ctx) => {
    const wallets = ctx.spy(walletsAtom)
    const selectedWalletIds = ctx.spy(selectedWalletIdsAtom)
    return wallets.filter((wallet) => selectedWalletIds.has(wallet.id))
  }).pipe(
    withOnUpdate((ctx) => {
      const transactionsWallet = ctx.get(transactionsWalletAtom)
      const selectedWallet = ctx.get(selectedWalletAtom)
      if (selectedWallet !== transactionsWallet) {
        transactionsWalletAtom(ctx, undefined)
      }
    }),
  )

  const selectedWalletAtom = atom((ctx) => {
    const wallets = ctx.spy(selectedWalletsAtom)
    return Array.single(wallets)
  })
  const transactionsWalletAtom = atom<WalletModel | undefined>(undefined)

  const toggleSelectedWalletAction = action((ctx, wallet: WalletModel) => {
    selectedWalletIdsAtom(ctx, Set.toggle(wallet.id))
  })

  const selectSingleWalletAction = action((ctx, wallet: WalletModel) => {
    selectedWalletIdsAtom(ctx, Set.create([wallet.id]))
  })

  const clearSelectedWalletAction = action((ctx) => {
    selectedWalletIdsAtom.reset(ctx)
  })

  onDisconnect(selectedWalletIdsAtom, (ctx) => {
    selectedWalletIdsAtom.reset(ctx)
  })

  const openEditWalletAction = action((ctx, wallet: WalletModel) => {
    selectSingleWalletAction(ctx, wallet)
    walletDetailsScreenAtom(ctx, 'edit')
  })

  const setTransactionsWalletAction = action(
    (ctx, wallet: WalletModel | undefined) => {
      if (wallet) {
        selectSingleWalletAction(ctx, wallet)
      }
      transactionsWalletAtom(ctx, wallet)
    },
  )

  return {
    selectedWalletIds: selectedWalletIdsAtom,
    selectedWallets: selectedWalletsAtom,
    selectedWallet: selectedWalletAtom,
    selectSingleWallet: selectSingleWalletAction,
    clearSelectedWallet: clearSelectedWalletAction,
    toggleSelectedWallet: toggleSelectedWalletAction,
    walletDetailsScreen: walletDetailsScreenAtom,
    transactionsWallet: transactionsWalletAtom,
    openEditWallet: openEditWalletAction,
    setTransactionsWallet: setTransactionsWalletAction,
  }
}

export const walletsManagerModelAtom = atom(reatomWalletsManager())

onConnect(walletsManagerModelAtom, (ctx) => {
  /*
   * The wallets themselves, which this screen is entirely about.
   *
   * `walletsByIdStateAtom` is `loadOnConnect: false`, so subscribing to the
   * list is not the same as asking for it, and nothing on this page asked.
   * The only two callers were the table's Retry button and the pulse board —
   * so the manager showed four skeleton rows that never resolved, and a count
   * of "0 wallets" that was not a count but an unasked question. Reached
   * through the board it happened to work, which is why it looked fine.
   *
   * Idempotent: a list already in hand does nothing.
   */
  loadWalletsAction(ctx)

  const unsubDeleted = walletDeletedEvent.onCall((__, _, [{ walletId }]) => {
    const model = ctx.get(walletsManagerModelAtom)
    const selectedWallet = ctx.get(model.selectedWallet)
    if (!selectedWallet) {
      return
    }
    if (walletId === selectedWallet.id) {
      model.clearSelectedWallet(ctx)
    }
  })

  return () => {
    unsubDeleted()
  }
})

startNewWalletFlowEvent.onCall((ctx) => {
  const model = ctx.get(walletsManagerModelAtom)
  routerActions.navigate(ctx, WalletManagerRoute.build())
  model.walletDetailsScreen(ctx, 'new-wallet')
})

startWalletDepositFlowEvent.onCall((ctx, { wallet }) => {
  const model = ctx.get(walletsManagerModelAtom)
  routerActions.navigate(ctx, WalletManagerRoute.build())
  model.selectSingleWallet(ctx, wallet)
  model.walletDetailsScreen(ctx, 'deposit')
})

startWalletTransferFlowEvent.onCall((ctx, { wallet }) => {
  const model = ctx.get(walletsManagerModelAtom)
  routerActions.navigate(ctx, WalletManagerRoute.build())
  model.selectSingleWallet(ctx, wallet)
  model.walletDetailsScreen(ctx, 'transfer')
})
